import { type ReportKpiInput, type ReportKpiSuccess, type ReportKpiRow, type APIError } from '@terros/common'
import { useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useServerRequest } from '../api'
import { useTantalimQuery } from '../query'
import { reportKpiKey } from '../keys'
import { type GetHookResponse } from '../hooks'

export function useReportKpi(input: ReportKpiInput, enabled = true): GetHookResponse<ReportKpiRow[]> {
  return useTantalimQuery<ReportKpiInput, ReportKpiSuccess, ReportKpiRow[]>({
    url: '/report/kpi',
    queryKey: reportKpiKey(input),
    input,
    convertResponse: (data) => {
      if (data.format === 'legacyTable') return data.rows
      // TODO - support other formats
      return []
    },
    options: {
      enabled,
    },
  })
}

export function useReportKpiV2(
  input: ReportKpiInput,
  options?: Omit<UseQueryOptions<ReportKpiSuccess, APIError>, 'queryKey'> | boolean
): GetHookResponse<ReportKpiSuccess> {
  if (typeof options === 'boolean') {
    options = { enabled: options }
  }
  input.version = 2
  return useTantalimQuery<ReportKpiInput, ReportKpiSuccess, ReportKpiSuccess>({
    url: '/report/kpi',
    queryKey: reportKpiKey(input),
    input,
    convertResponse: (data) => data,
    options,
  })
}

export function useFetchReportKpiV2(): (input: ReportKpiInput) => Promise<ReportKpiSuccess> {
  const serverRequest = useServerRequest<ReportKpiInput, ReportKpiSuccess>('/report/kpi')
  const queryClient = useQueryClient()
  return useCallback(
    async (input: ReportKpiInput): Promise<ReportKpiSuccess> => {
      const request: ReportKpiInput = { ...input, version: 2 }
      return queryClient.fetchQuery({
        queryKey: reportKpiKey(request),
        queryFn: () => serverRequest(request),
      })
    },
    [queryClient, serverRequest]
  )
}
